import { Button, Input } from "@/components/ui";
import { QuickFilter } from "@/components/ui/FilterBar/FilterBar";
import { retrieveIncidentTypeCategoriesQueryOptions } from "@/lib/data/react-query/matters/matter-type-category";
import type { FirmReferralDirectoryEntry } from "@/lib/data/requests/firms/referral-profile";
import { US_STATES } from "@/lib/utils/states";
import { useQuery } from "@tanstack/react-query";

export type ReferralNetworkFilterValues = {
  practiceAreaIds: string[];
  search: string;
  serviceAreas: string[];
};

type ReferralNetworkFiltersProps = ReferralNetworkFilterValues & {
  onChange: (filters: ReferralNetworkFilterValues) => void;
};

export const matchesReferralNetworkFilters = (
  firm: FirmReferralDirectoryEntry,
  filters: ReferralNetworkFilterValues,
) => {
  const normalizedSearch = filters.search.trim().toLowerCase();
  const matchesSearch =
    !normalizedSearch || firm.name.toLowerCase().includes(normalizedSearch);
  const matchesPracticeArea =
    filters.practiceAreaIds.length === 0 ||
    firm.practiceAreas.some((area) =>
      filters.practiceAreaIds.includes(area.id),
    );
  const matchesServiceArea =
    filters.serviceAreas.length === 0 ||
    firm.serviceAreas.some((state) => filters.serviceAreas.includes(state));

  return matchesSearch && matchesPracticeArea && matchesServiceArea;
};

export function ReferralNetworkFilters({
  onChange,
  practiceAreaIds,
  search,
  serviceAreas,
}: ReferralNetworkFiltersProps) {
  const categories = useQuery(retrieveIncidentTypeCategoriesQueryOptions());
  const hasFilters = Boolean(
    search.trim() || practiceAreaIds.length || serviceAreas.length,
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label="Search partner firms"
          className="h-8 w-full sm:w-64"
          maxLength={200}
          placeholder="Search firms..."
          value={search}
          onChange={(event) => {
            onChange({
              practiceAreaIds,
              search: event.target.value,
              serviceAreas,
            });
          }}
        />
        <QuickFilter
          filterOption={{
            label: `Practice areas${practiceAreaIds.length ? ` (${practiceAreaIds.length})` : ""}`,
            value: "practiceAreaIds",
            input: {
              type: "searchable-select",
              items: (categories.data?.data ?? []).map((item) => ({
                value: item.id,
                label: item.label,
              })),
            },
          }}
          selectedValues={practiceAreaIds}
          onAddFilter={(_key, value) => {
            onChange({
              practiceAreaIds: [...new Set([...practiceAreaIds, value])],
              search,
              serviceAreas,
            });
          }}
          onRemoveFilter={(_key, value) => {
            onChange({
              practiceAreaIds: practiceAreaIds.filter((item) => item !== value),
              search,
              serviceAreas,
            });
          }}
        />
        <QuickFilter
          filterOption={{
            label: `Service states${serviceAreas.length ? ` (${serviceAreas.length})` : ""}`,
            value: "serviceAreas",
            input: {
              type: "searchable-select",
              items: US_STATES.map((state) => ({
                value: state,
                label: state,
              })),
            },
          }}
          selectedValues={serviceAreas}
          onAddFilter={(_key, value) => {
            onChange({
              practiceAreaIds,
              search,
              serviceAreas: [...new Set([...serviceAreas, value])],
            });
          }}
          onRemoveFilter={(_key, value) => {
            onChange({
              practiceAreaIds,
              search,
              serviceAreas: serviceAreas.filter((item) => item !== value),
            });
          }}
        />
        {hasFilters ? (
          <Button
            variant="ghost"
            className="h-8 rounded-full px-3 text-xs"
            onClick={() => {
              onChange({ practiceAreaIds: [], search: "", serviceAreas: [] });
            }}
          >
            Clear
          </Button>
        ) : null}
      </div>
      {categories.isError ? (
        <p role="alert" className="text-sm">
          Practice areas could not be loaded.{" "}
          <Button
            variant="outline"
            onClick={() => {
              void categories.refetch();
            }}
          >
            Retry
          </Button>
        </p>
      ) : null}
    </div>
  );
}
